import React, { useState, useMemo } from 'react';
import { SurveyConfig, SurveyQuestion, SurveyCategory } from '../../../config/surveys/schema';
import SidebarProgressBar from './SidebarProgressBar';
import SidebarCategorySection from './SidebarCategorySection';
import SidebarQuestionItem from './SidebarQuestionItem';
import './SurveySidebar.css';

interface SurveySidebarProps {
  config: SurveyConfig;
  currentSection: number;
  unlockedSections: Set<number>;
  answers: Record<string, string>;
  onNavigate: (sectionIndex: number) => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface CategoryWithQuestions {
  category: SurveyCategory;
  questions: SurveyQuestion[];
  startIndex: number;
}

const SurveySidebar: React.FC<SurveySidebarProps> = ({
  config,
  currentSection,
  unlockedSections,
  answers,
  onNavigate,
  isMobileOpen,
  onMobileClose,
  isCollapsed,
  onToggleCollapse,
}) => {
  // Track expanded categories - all expanded by default
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    const categoryIds = config.categories?.map(c => c.id) || [];
    categoryIds.push('uncategorized');
    return new Set(categoryIds);
  });

  // Compute answered question IDs
  const answeredQuestionIds = useMemo(() => {
    return new Set(Object.keys(answers).filter(id => answers[id] !== ''));
  }, [answers]);

  // Compute accessible question IDs based on unlocked sections
  const accessibleQuestionIds = useMemo(() => {
    const accessible = new Set<string>();
    config.questions.forEach((q, idx) => {
      const sectionIdx = idx + 1;
      if (unlockedSections.has(sectionIdx)) {
        accessible.add(q.id);
      }
    });
    return accessible;
  }, [config.questions, unlockedSections]);

  // Current question ID
  const currentQuestionId = useMemo(() => {
    if (currentSection === 0 || currentSection >= config.questions.length + 1) {
      return null;
    }
    return config.questions[currentSection - 1]?.id || null;
  }, [config.questions, currentSection]);

  // Group questions by category with their start indices
  const categorizedQuestions = useMemo((): CategoryWithQuestions[] => {
    const result: CategoryWithQuestions[] = [];
    const questionIndexMap = new Map<string, number>();

    // Map each question ID to its index
    config.questions.forEach((q, idx) => {
      questionIndexMap.set(q.id, idx);
    });

    // Sort categories by order
    const sortedCategories = [...(config.categories || [])].sort(
      (a, b) => (a.order || 0) - (b.order || 0)
    );

    // Group questions by category
    const questionsByCategory = new Map<string, SurveyQuestion[]>();
    config.questions.forEach(q => {
      const catId = q.categoryId || 'uncategorized';
      const existing = questionsByCategory.get(catId) || [];
      existing.push(q);
      questionsByCategory.set(catId, existing);
    });

    // Build result with start indices
    sortedCategories.forEach(category => {
      const questions = questionsByCategory.get(category.id) || [];
      if (questions.length > 0) {
        const startIndex = questionIndexMap.get(questions[0].id) || 0;
        result.push({ category, questions, startIndex });
      }
    });

    // Add uncategorized if exists
    const uncategorized = questionsByCategory.get('uncategorized');
    if (uncategorized && uncategorized.length > 0) {
      const startIndex = questionIndexMap.get(uncategorized[0].id) || 0;
      result.push({
        category: { id: 'uncategorized', name: 'Other Questions' },
        questions: uncategorized,
        startIndex,
      });
    }

    return result;
  }, [config.questions, config.categories]);

  // Progress calculations
  const answeredCount = Object.keys(answers).filter(k => answers[k] !== '').length;
  const totalRequired = config.questions.filter(q => q.required !== false).length;
  const percent = totalRequired > 0 ? Math.round((answeredCount / totalRequired) * 100) : 0;

  // Find next unanswered question
  const handleJumpToNext = () => {
    for (let i = 0; i < config.questions.length; i++) {
      const question = config.questions[i];
      const sectionIdx = i + 1;

      if (unlockedSections.has(sectionIdx)) {
        const answer = answers[question.id];
        if (question.required !== false && (!answer || answer === '')) {
          onNavigate(sectionIdx);
          onMobileClose();
          return;
        }
      }
    }

    // If all answered, go to furthest unlocked
    const furthest = Math.max(...Array.from(unlockedSections));
    onNavigate(furthest);
    onMobileClose();
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleQuestionClick = (sectionIndex: number) => {
    if (unlockedSections.has(sectionIndex)) {
      onNavigate(sectionIndex);
      onMobileClose();
    }
  };

  // Check if we have categories or should show flat list
  const hasCategories = categorizedQuestions.length > 0 &&
    !(categorizedQuestions.length === 1 && categorizedQuestions[0].category.id === 'uncategorized');

  return (
    <>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${isMobileOpen ? 'visible' : ''}`}
        onClick={onMobileClose}
      />

      <aside className={`survey-sidebar ${isMobileOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <h3 className="sidebar-title">Survey Progress</h3>
          <button className="collapse-toggle-btn" onClick={onToggleCollapse} aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="sidebar-close-btn" onClick={onMobileClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <SidebarProgressBar
          current={answeredCount}
          total={totalRequired}
          percent={percent}
        />

        <nav className="sidebar-nav">
          {hasCategories ? (
            // Render with category sections
            categorizedQuestions.map(({ category, questions, startIndex }) => (
              <SidebarCategorySection
                key={category.id}
                category={category}
                questions={questions}
                questionStartIndex={startIndex}
                currentQuestionId={currentQuestionId}
                answeredQuestionIds={answeredQuestionIds}
                accessibleQuestionIds={accessibleQuestionIds}
                onQuestionClick={handleQuestionClick}
                isExpanded={expandedCategories.has(category.id)}
                onToggleExpand={() => toggleCategory(category.id)}
              />
            ))
          ) : (
            // Flat list without categories
            <div className="questions-list">
              {config.questions.map((question, idx) => {
                const sectionIndex = idx + 1;
                return (
                  <SidebarQuestionItem
                    key={question.id}
                    question={question}
                    questionNumber={idx + 1}
                    sectionIndex={sectionIndex}
                    isCurrent={question.id === currentQuestionId}
                    isAnswered={answeredQuestionIds.has(question.id)}
                    isAccessible={accessibleQuestionIds.has(question.id)}
                    onClick={() => handleQuestionClick(sectionIndex)}
                  />
                );
              })}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <button
            className="jump-to-next-btn"
            onClick={handleJumpToNext}
          >
            Jump to Current Question
          </button>
        </div>
      </aside>
    </>
  );
};

export default SurveySidebar;
